/**
 * Centralized Unsplash image registry.
 *
 * Every photo below is licensed under the free Unsplash License
 * (https://unsplash.com/license) - use is permitted for commercial and
 * non-commercial purposes without attribution, though attribution is
 * appreciated and is what `photographerName` / `unsplashUrl` enable.
 *
 * Adding a new photo:
 *   1. Find a free photo on unsplash.com (skip anything marked Unsplash+).
 *   2. Read the JSON metadata at `https://unsplash.com/napi/photos/<short-id>`.
 *   3. Add a new entry here and reference it from the relevant data module.
 */

export interface UnsplashPhoto {
  unsplashId: string;
  cdnHash: string;
  photographerName: string;
  photographerUsername: string;
  altText: string;
}

const photo = (p: UnsplashPhoto) => p;

export const IMAGES = {
  'olive-oil-pour': photo({
    unsplashId: 'qMX3AdGwm0o',
    cdnHash: 'photo-1695606452817-e28a67339e57',
    photographerName: 'Daniel',
    photographerUsername: 'unsplashbydan',
    altText: 'Olive oil poured over a tasting plate with herbs and bread',
  }),
  'olive-grove-light': photo({
    unsplashId: 'G1p0feFHhzo',
    cdnHash: 'photo-1653387131916-698a5f4ec64c',
    photographerName: 'Ramzi Belaidi',
    photographerUsername: 'ramzibelaidi',
    altText: 'Rows of olive trees in soft afternoon light',
  }),
  'sunlit-orchard-road': photo({
    unsplashId: '0b0lC3ddQ-w',
    cdnHash: 'photo-1694644566527-557c4d6699b9',
    photographerName: 'Rin Tian',
    photographerUsername: 'rrrrin_t',
    altText: 'A narrow road cutting through a sunlit orchard landscape',
  }),
  'olive-orchard-field': photo({
    unsplashId: 'jeC5QpsCKVc',
    cdnHash: 'photo-1554014049-410432169415',
    photographerName: 'Kevin Wenning',
    photographerUsername: 'kevinwenning',
    altText: 'View across an olive orchard under a clear sky',
  }),
  'olive-harvest': photo({
    unsplashId: 'PldIQOrEaKU',
    cdnHash: 'photo-1762970443992-af44721f8229',
    photographerName: 'National Gallery of Art',
    photographerUsername: 'nationalgallery',
    altText: 'Two figures harvesting olives from mature trees',
  }),
  'olive-oil-shelf': photo({
    unsplashId: 'W1OjZrc7XAo',
    cdnHash: 'photo-1757801333112-7b89af15c7fe',
    photographerName: 'Zoshua Colah',
    photographerUsername: 'zoshuacolah',
    altText: 'Bottles of olive oil arranged on a shelf',
  }),
  'olive-tasting-plate': photo({
    unsplashId: '48powiIBvDo',
    cdnHash: 'photo-1750396628419-ff80446706e3',
    photographerName: 'Huy van',
    photographerUsername: 'hvan01611',
    altText: 'Bread, dips, and olive oil displayed for tasting',
  }),
  'olive-branch-close': photo({
    unsplashId: 'eUrUzQ6gXE8',
    cdnHash: 'photo-1759566817518-23e1216ed15e',
    photographerName: 'Denis N.',
    photographerUsername: 'ndcphoto',
    altText: 'Close-up of olive tree branches and green leaves',
  }),
  'olive-hills': photo({
    unsplashId: 'Ri47-Tx2drk',
    cdnHash: 'photo-1773785429119-2514f3931430',
    photographerName: 'Viacheslav Poturaev',
    photographerUsername: 'vearutop',
    altText: 'Rolling hills planted with olive trees',
  }),
  'olive-bottle-counter': photo({
    unsplashId: '47Pg5Eswnnk',
    cdnHash: 'photo-1638324396229-632af05042dd',
    photographerName: 'Addilyn Ragsdill @clockworklemon.com',
    photographerUsername: 'clockwork_lemon',
    altText: 'Bottle of olive oil on a tasting counter',
  }),
  'single-olive-branch': photo({
    unsplashId: 'bK-T_lTGj28',
    cdnHash: 'photo-1759670966397-23826c993a2d',
    photographerName: 'Sumeyye Sue Bozkus',
    photographerUsername: 'sumeyyebozkus',
    altText: 'Single green olive hanging from a branch',
  }),
} as const;

export type ImageKey = keyof typeof IMAGES;

export function imageUrl(key: ImageKey, opts: { w?: number; q?: number } = {}): string {
  const { w = 1200, q = 80 } = opts;
  const meta = IMAGES[key];
  const base = meta.cdnHash.startsWith('http')
    ? meta.cdnHash
    : `https://images.unsplash.com/${meta.cdnHash}`;
  return `${base}?auto=format&fit=crop&w=${w}&q=${q}`;
}

export function imageCredit(key: ImageKey): {
  photographerName: string;
  photographerUrl: string;
  unsplashUrl: string;
  altText: string;
} {
  const meta = IMAGES[key];
  return {
    photographerName: meta.photographerName,
    photographerUrl: `https://unsplash.com/@${meta.photographerUsername}?utm_source=olive_atlas&utm_medium=referral`,
    unsplashUrl: `https://unsplash.com/photos/${meta.unsplashId}?utm_source=olive_atlas&utm_medium=referral`,
    altText: meta.altText,
  };
}
